import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Download, Link2, Check, Image, ChevronDown, Sparkles, X, Upload, Plus, Square, RectangleHorizontal, RectangleVertical, Film, FolderPlus } from 'lucide-react';
import VideoModule from './VideoModule';
import StoryboardModule from './storyboard/StoryboardModule';
import PromptVoiceField from '@/components/shared/PromptVoiceField';
import PromptAssistant from '@/components/shared/PromptAssistant';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_REF_BYTES = 8 * 1024 * 1024; // 8 MB per image cap

// XHR JSON helper. We avoid window.fetch because the Emergent platform script
// monkey-patches it to eagerly read response bodies for logging, which breaks
// any of our `await res.json()` calls with "body stream already read".
// XMLHttpRequest is not affected by that patch.
function xhrJson(method, url, body, timeoutMs = 180000, headers = {}) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.timeout = timeoutMs;
    xhr.onload = () => {
      let data = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
      catch { data = { detail: xhr.responseText }; }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, data: { detail: 'Network error' } });
    xhr.ontimeout = () => resolve({ ok: false, status: 0, data: { detail: 'Request timed out' } });
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

// Multipart upload via XHR (FormData) — also avoids the platform fetch patch.
function xhrUpload(url, formData, headers = {}) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.timeout = 60000;
    xhr.onload = () => {
      let data = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
      catch { data = { detail: xhr.responseText }; }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, data: { detail: 'Network error' } });
    xhr.ontimeout = () => resolve({ ok: false, status: 0, data: { detail: 'Upload timed out' } });
    xhr.send(formData);
  });
}

export default function ProductionModule() {
  const {
    projects,
    productionRef,
    setProductionRef,
  } = useApp();
  const { session } = useAuth();
  const accessToken = session?.access_token || null;
  const authHeader = useMemo(
    () => (accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    [accessToken]
  );
  const [prompt, setPrompt] = useState('');
  const [styleNotes, setStyleNotes] = useState('');
  const [refDesc, setRefDesc] = useState('');
  const [numVariations, setNumVariations] = useState(2);
  const [aspectRatio, setAspectRatio] = useState('square'); // square | vertical | horizontal
  const [projectId, setProjectId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [savingIdx, setSavingIdx] = useState(null);
  const [savedIdxs, setSavedIdxs] = useState({}); // map id -> true
  const [error, setError] = useState('');
  const [tab, setTab] = useState('generate'); // 'generate' | 'video' | 'history'
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  // Per-project reference images, fetched live from Supabase via the backend.
  const [currentRefs, setCurrentRefs] = useState([]);
  const [refsLoading, setRefsLoading] = useState(false);
  const promptRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  // Refresh the references list:
  //   - If a project is selected → /api/projects/{id}/files?source=team
  //   - Otherwise → /api/storage/unlinked (manager's scratch space)
  // Returns the resulting array (or [] on failure) so callers can compute
  // diffs for auto-selection of newly uploaded items.
  const reloadRefs = useCallback(async () => {
    if (!accessToken) {
      setCurrentRefs([]);
      return [];
    }
    setRefsLoading(true);
    const cb = `_=${Date.now()}`;
    const url = projectId
      ? `${API}/api/projects/${projectId}/files?source=team&file_type=image&${cb}`
      : `${API}/api/storage/unlinked?${cb}`;
    const { ok, data } = await xhrJson('GET', url, null, 30000, authHeader);
    let next = [];
    if (ok && Array.isArray(data.files)) {
      next = data.files.map(f => ({
        id: f.id,
        name: f.name,
        dataUrl: f.url,
        size: f.size_bytes,
        type: 'image',
        uploadedAt: f.created_at,
        storage_path: f.storage_path,
        // `linked: false` for unlinked refs — uses storage_path as identifier
        linked: f.linked !== false && !String(f.id).startsWith('unlinked/'),
      }));
    }
    setCurrentRefs(next);
    setRefsLoading(false);
    return next;
  }, [projectId, accessToken, authHeader]);

  // Refetch when project changes or auth becomes available
  useEffect(() => {
    reloadRefs();
  }, [reloadRefs]);

  const handleFiles = async (fileList) => {
    setUploadError('');
    if (!accessToken) {
      setUploadError('Session not found. Sign in as a manager to upload references.');
      return;
    }
    const files = Array.from(fileList || []).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;

    setUploading(true);
    // Snapshot existing ref ids — anything new after reload is what we just uploaded.
    const beforeIds = new Set(currentRefs.map(r => r.id));
    try {
      let firstError = '';
      for (const file of files) {
        if (file.size > MAX_REF_BYTES) {
          firstError = firstError || `"${file.name}" supera 8 MB y fue omitido.`;
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        // If a project is selected, link directly. Otherwise push to manager's
        // unlinked scratch space (project-files/unlinked/{user_id}/...).
        const url = projectId
          ? `${API}/api/storage/upload/${projectId}`
          : `${API}/api/storage/upload-unlinked`;
        if (projectId) {
          fd.append('bucket', 'project-files');
          fd.append('source', 'team');
        }
        const { ok, status, data } = await xhrUpload(url, fd, authHeader);
        if (!ok) {
          firstError = firstError || (data?.detail || `Upload failed (${status}) for ${file.name}`);
        }
      }
      if (firstError) setUploadError(firstError);
      const refreshed = await reloadRefs();
      // Auto-select the newly uploaded references so the very next Generar click
      // takes the EXACT EDIT path. Without this, users uploaded a reference
      // expecting "edit this image" and got text-to-image because the thumbnail
      // wasn't clicked. (Field bug, Feb 2026.)
      const list = Array.isArray(refreshed) ? refreshed : null;
      if (list) {
        const newlyUploaded = list.filter(r => !beforeIds.has(r.id)).map(r => r.id);
        if (newlyUploaded.length > 0) {
          setSelectedRefIds(prev => Array.from(new Set([...prev, ...newlyUploaded])));
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const removeRef = async (refId) => {
    setUploadError('');
    const ref = currentRefs.find(r => r.id === refId);
    setCurrentRefs(prev => prev.filter(r => r.id !== refId));
    setSelectedRefIds(prev => prev.filter(x => x !== refId));

    let result;
    if (ref?.linked === false) {
      // Unlinked: identifier IS the storage_path
      result = await xhrJson(
        'DELETE',
        `${API}/api/storage/unlinked?path=${encodeURIComponent(refId)}`,
        null,
        15000,
        authHeader,
      );
    } else {
      result = await xhrJson(
        'DELETE',
        `${API}/api/storage/files/${refId}`,
        null,
        15000,
        authHeader,
      );
    }
    if (!result.ok) {
      setUploadError(result.data?.detail || `Delete failed (${result.status})`);
      reloadRefs();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Refs the user has selected (clicked) → trigger /edit instead of /images
  const [selectedRefIds, setSelectedRefIds] = useState([]);

  // Async job queue (production background workers). A job starts immediately,
  // the UI is freed, and we poll every 3s until it completes. The most recent
  // completed job's images are auto-loaded into the Results grid.
  const [jobs, setJobs] = useState([]);
  const lastCompletedJobIdRef = useRef(null);

  useEffect(() => {
    if (!accessToken) { setJobs([]); return undefined; }
    let cancelled = false;
    const tick = async () => {
      const { ok, data } = await xhrJson(
        'GET',
        `${API}/api/generate/jobs?limit=20&_=${Date.now()}`,
        null,
        20000,
        authHeader,
      );
      if (cancelled || !ok) return;
      const list = Array.isArray(data.jobs) ? data.jobs : [];
      setJobs(list);
      const latestDone = list.find(j => j.status === 'completed');
      if (latestDone && latestDone.id !== lastCompletedJobIdRef.current) {
        lastCompletedJobIdRef.current = latestDone.id;
        const assets = Array.isArray(latestDone.result_asset_ids) ? latestDone.result_asset_ids : [];
        if (assets.length) {
          setResults(assets);
          setError('');
        }
      }
    };
    tick();
    const interval = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [accessToken, authHeader]);

  const toggleRefSelection = (refId) => {
    setSelectedRefIds(prev =>
      prev.includes(refId) ? prev.filter(x => x !== refId) : [...prev, refId]
    );
  };

  // Reset selection when project changes
  useEffect(() => {
    setSelectedRefIds([]);
  }, [projectId]);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setError('');

    if (!accessToken) {
      setError('Session expired. Please sign in again.');
      return;
    }

    try {
      // Dispatch rule (kept in sync with /jobs backend):
      //   selectedRefIds.length > 0 → mode='edit'  (/v1/images/edits)
      //   else                      → mode='generation' (/v1/images/generations)
      const useEdit = selectedRefIds.length > 0;
      const selectedRefs = currentRefs.filter(r => selectedRefIds.includes(r.id));
      const linkedIds = selectedRefs.filter(r => r.linked !== false).map(r => r.id);
      const unlinkedPaths = selectedRefs.filter(r => r.linked === false).map(r => r.storage_path);

      const payload = {
        prompt: prompt.trim(),
        project_id: projectId || null,
        aspect_ratio: aspectRatio,
        num_variations: numVariations,
        reference_ids: linkedIds,
        reference_storage_paths: unlinkedPaths,
        // Edit mode: NO style_notes / reference_description (avoid reinterpretation).
        // Text-to-image mode: pass them through.
        style_notes: useEdit ? null : (styleNotes.trim() || null),
        reference_description: useEdit ? null : (refDesc.trim() || null),
      };

      // eslint-disable-next-line no-console
      console.info(
        `[Production] QUEUE JOB → ${useEdit ? 'EXACT EDIT' : 'TEXT-TO-IMAGE'}`,
        { refs: selectedRefs.length, prompt: payload.prompt },
      );

      setGenerating(true);
      const { ok, status, data } = await xhrJson(
        'POST', `${API}/api/generate/jobs`, payload, 30000, authHeader,
      );
      setGenerating(false);

      if (!ok) {
        const detail = typeof data?.detail === 'string' ? data.detail : null;
        setError(
          detail ||
          (status === 0 ? 'Network error — check your connection and try again.' :
           status === 401 ? 'Session expired. Please sign in again.' :
           status === 413 ? 'Image too large. Use one under 8 MB and retry.' :
           status === 429 ? 'Too many active jobs. Wait for some to finish.' :
           `Could not start generation (HTTP ${status || '?'}).`)
        );
        return;
      }
      // Optimistic: show the new job immediately; polling will refresh state.
      if (data?.job) setJobs(prev => [data.job, ...prev.filter(j => j.id !== data.job.id)]);
      // UI is freed: clear the prompt so the manager can immediately type the
      // next brief. Keep refs selected in case they want to chain edits.
      setPrompt('');
    } catch (err) {
      setGenerating(false);
      // eslint-disable-next-line no-console
      console.error('[Production] handleGenerate fatal:', err);
      setError('Unexpected error starting generation. Please try again.');
    }
  };

  const saveToProject = async (idx) => {
    const r = results[idx];
    if (!r) return;
    if (!projectId) {
      setError('Select a project to save the image.');
      return;
    }
    setSavingIdx(idx);
    const { ok, data } = await xhrJson('POST', `${API}/api/generate/save-to-project`, {
      storage_path: r.storage_path || null,
      image_base64: r.base64 || null,
      name: r.name,
      project_id: projectId,
      project_label: projects.find(p => p.id === projectId)?.name || null,
      prompt,
      aspect_ratio: aspectRatio,
    }, 30000, authHeader);
    if (ok) {
      setSavedIdxs(prev => ({ ...prev, [r.id]: true }));
    } else {
      setError(data?.detail || 'Could not save to project');
    }
    setSavingIdx(null);
  };

  const sendToVideoGen = (r) => {
    // Stage the image as productionRef so the (upcoming) video generation tool can pick it up
    setProductionRef({
      url: r.url || (r.base64 ? `data:image/png;base64,${r.base64}` : null),
      name: r.name,
      storage_path: r.storage_path,
      aspect_ratio: r.aspect_ratio,
      target: 'video',
    });
    // simple visual feedback via flag in saved map (reusing color)
    setSavedIdxs(prev => ({ ...prev, [`v-${r.id}`]: true }));
  };

  const handleDownload = (r) => {
    const href = r.url || (r.base64 ? `data:image/png;base64,${r.base64}` : null);
    if (!href) return;
    const link = document.createElement('a');
    link.href = href;
    link.download = (r.name || 'generated').replace(/\s+/g, '_');
    link.target = '_blank';
    link.rel = 'noopener';
    link.click();
  };

  const loadHistory = async () => {
    setHistoryLoaded(false);
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    const { ok, data } = await xhrJson('GET', `${API}/api/generate/history${qs}`, null);
    setHistory(ok ? (data.assets || []) : []);
    setHistoryLoaded(true);
  };

  const clientProjects = projects;

  return (
    <div className="flex flex-col h-full" data-testid="production-module">
      {/* Tab header */}
      <div className="px-4 md:px-6 py-3 border-b border-gray-200/60 flex items-center gap-4">
        <button
          data-testid="tab-generate"
          onClick={() => setTab('generate')}
          className={`text-sm font-medium transition-colors ${tab === 'generate' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
        >
          Imagen
        </button>
        <button
          data-testid="tab-video"
          onClick={() => setTab('video')}
          className={`text-sm font-medium transition-colors ${tab === 'video' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
        >
          Video
        </button>
        <button
          data-testid="tab-storyboard"
          onClick={() => setTab('storyboard')}
          className={`text-sm font-medium transition-colors ${tab === 'storyboard' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
        >
          Storyboard
        </button>
        <button
          data-testid="tab-history"
          onClick={() => { setTab('history'); loadHistory(); }}
          className={`text-sm font-medium transition-colors ${tab === 'history' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
        >
          Historial
        </button>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        {tab === 'video' ? (
          <VideoModule projectId={projectId} />
        ) : tab === 'storyboard' ? (
          <StoryboardModule />
        ) : tab === 'generate' ? (
          <div className="p-4 md:p-6 max-w-4xl space-y-5">
            {/* Prompt */}
            <div className="space-y-2">
              <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Prompt</label>
              <PromptAssistant
                endpoint="image"
                testIdPrefix="image-assist"
                hasContext={currentRefs.length > 0}
                contextHint="Upload at least one image to 'Reference images' to get started."
                contextPayload={{
                  // Watch ALL uploaded images automatically — not just selected.
                  image_storage_paths: currentRefs.map(r => r.storage_path).filter(Boolean),
                  bucket: 'project-files',
                }}
                onUsePrompt={(text) => {
                  setPrompt(text);
                  requestAnimationFrame(() => promptRef.current?.focus());
                }}
              />
              <PromptVoiceField
                value={prompt}
                onChange={setPrompt}
                testIdPrefix="gen-prompt-voice"
                hint="Prompt for image generation or editing with gpt-image-2 in Ruido.ai."
              >
                <textarea
                  ref={promptRef}
                  data-testid="gen-prompt"
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder="Describe the image you want to generate..."
                  className="w-full px-4 py-3 pr-11 rounded-xl bg-white/50 border border-gray-200/50 text-sm text-gray-900 outline-none focus:border-gray-300 placeholder:text-gray-400 resize-none min-h-[100px]"
                />
              </PromptVoiceField>
            </div>

            {/* Style + Reference — side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Style notes</label>
                <textarea
                  data-testid="gen-style"
                  value={styleNotes}
                  onChange={e => setStyleNotes(e.target.value)}
                  placeholder="e.g., Cinematic, warm tones, 35mm film"
                  className="w-full px-3 py-2.5 rounded-xl bg-white/40 border border-gray-200/40 text-sm text-gray-700 outline-none focus:border-gray-300 placeholder:text-gray-400 resize-none min-h-[60px]"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Reference description</label>
                <textarea
                  data-testid="gen-reference"
                  value={refDesc}
                  onChange={e => setRefDesc(e.target.value)}
                  placeholder="e.g., Similar to Apple product photography"
                  className="w-full px-3 py-2.5 rounded-xl bg-white/40 border border-gray-200/40 text-sm text-gray-700 outline-none focus:border-gray-300 placeholder:text-gray-400 resize-none min-h-[60px]"
                />
              </div>
            </div>

            {/* Variations + Project linking + Aspect ratio */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Variations</label>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {[1, 2, 3, 4, 6, 8].map(n => (
                    <button
                      key={n}
                      data-testid={`gen-var-${n}`}
                      onClick={() => setNumVariations(n)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        numVariations === n
                          ? 'bg-gray-900 text-white'
                          : 'bg-white/40 border border-gray-200/40 text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Formato</label>
                <div className="flex items-center gap-1.5">
                  {[
                    { id: 'square', label: '1:1', Icon: Square },
                    { id: 'vertical', label: '9:16', Icon: RectangleVertical },
                    { id: 'horizontal', label: '16:9', Icon: RectangleHorizontal },
                  ].map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      data-testid={`gen-aspect-${id}`}
                      onClick={() => setAspectRatio(id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        aspectRatio === id
                          ? 'bg-gray-900 text-white'
                          : 'bg-white/40 border border-gray-200/40 text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Vincular al proyecto</label>
                <select
                  data-testid="gen-project"
                  value={projectId}
                  onChange={e => setProjectId(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-white/40 border border-gray-200/40 text-sm text-gray-700 outline-none"
                >
                  <option value="">Sin proyecto</option>
                  {clientProjects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Reference images — manual uploads per project */}
            <div
              data-testid="ref-section"
              onDrop={handleDrop}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              className={`relative rounded-2xl border transition-all ${
                isDragging
                  ? 'border-gray-900 bg-white/70'
                  : 'border-gray-200/50 bg-white/30'
              }`}
            >
              <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 uppercase tracking-wider">
                    Reference images
                  </span>
                  {currentRefs.length > 0 && (
                    <span className="text-[10px] text-gray-400">· {currentRefs.length}</span>
                  )}
                  {selectedRefIds.length > 0 && (
                    <span className="text-[10px] text-gray-900 font-medium">
                      · {selectedRefIds.length} selected
                    </span>
                  )}
                </div>
                <button
                  data-testid="ref-upload-btn"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/60 border border-gray-200/60 text-gray-700 hover:text-gray-900 hover:bg-white/80 transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  {uploading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  Upload reference image
                </button>
                <input
                  ref={fileInputRef}
                  data-testid="ref-file-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    handleFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>

              <div className="px-4 pb-4">
                {currentRefs.length === 0 ? (
                  <div className="py-6 text-center">
                    <div className="mx-auto w-9 h-9 rounded-full bg-white/60 border border-gray-200/60 flex items-center justify-center mb-2">
                      <Image className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500">
                      Drag images here or upload from your device.
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {projectId
                        ? 'Logos, products, screenshots, moodboards or visual inspiration.'
                        : 'No project: references stay in your personal space.'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                    {currentRefs.map((r) => {
                      const selected = selectedRefIds.includes(r.id);
                      return (
                      <div
                        key={r.id}
                        data-testid={`ref-item-${r.id}`}
                        onClick={() => toggleRefSelection(r.id)}
                        className={`group relative aspect-square rounded-xl overflow-hidden border bg-white/40 cursor-pointer transition-all ${
                          selected
                            ? 'border-gray-900 shadow-md ring-2 ring-gray-900/20'
                            : 'border-gray-200/50 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={r.dataUrl}
                          alt={r.name}
                          className="w-full h-full object-cover"
                        />
                        {selected && (
                          <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-gray-900 flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                        <button
                          type="button"
                          aria-label="Remove reference"
                          data-testid={`ref-remove-${r.id}`}
                          onClick={(e) => { e.stopPropagation(); removeRef(r.id); }}
                          className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                        <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-black/55 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-[10px] text-white truncate block">{r.name}</span>
                        </div>
                      </div>
                    );
                    })}
                    <button
                      type="button"
                      data-testid="ref-add-tile"
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square rounded-xl border border-dashed border-gray-300/70 bg-white/20 flex flex-col items-center justify-center text-gray-400 hover:text-gray-700 hover:border-gray-400 transition-all"
                    >
                      <Plus className="w-4 h-4 mb-0.5" />
                      <span className="text-[10px]">Add</span>
                    </button>
                  </div>
                )}

                {uploadError && (
                  <p className="mt-2 text-[11px] text-red-500">{uploadError}</p>
                )}
              </div>

              {isDragging && (
                <div className="pointer-events-none absolute inset-0 rounded-2xl border-2 border-dashed border-gray-900/70 bg-white/60 backdrop-blur-sm flex items-center justify-center">
                  <div className="flex items-center gap-2 text-sm text-gray-800">
                    <Upload className="w-4 h-4" />
                    Suelta para subir
                  </div>
                </div>
              )}
            </div>

            {/* Generate button + live status */}
            <div className="flex items-center gap-3 flex-wrap">
              <button
                data-testid="gen-submit"
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all active:scale-[0.98] ${
                  generating || !prompt.trim()
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enviando…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generar {numVariations} {numVariations > 1 ? 'imágenes' : 'imagen'}
                  </>
                )}
              </button>
              {/* Internal/debug indicator: confirms the dispatch path the next
                  Generar click will take. Visible only when refs are selected. */}
              {selectedRefIds.length > 0 && (
                <span
                  data-testid="exact-edit-mode-badge"
                  title="OpenAI /v1/images/edits con la imagen seleccionada como campo `image`"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Modo edición exacta · {selectedRefIds.length} ref{selectedRefIds.length !== 1 ? 's' : ''}
                </span>
              )}
              {(() => {
                const active = jobs.filter(j => j.status === 'queued' || j.status === 'processing').length;
                if (!active) return null;
                return (
                  <span
                    data-testid="active-jobs-counter"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-900/[0.06] text-gray-700 border border-gray-200/70"
                  >
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {active} job{active !== 1 ? 's' : ''} activo{active !== 1 ? 's' : ''}
                  </span>
                );
              })()}
            </div>

            {/* Jobs panel — shows queued/processing/failed. Completed jobs
                auto-promote to the Resultados grid below and are hidden here. */}
            {jobs.some(j => ['queued', 'processing', 'failed'].includes(j.status)) && (
              <div data-testid="jobs-panel" className="space-y-1.5">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider">Cola de trabajos</h3>
                {jobs
                  .filter(j => ['queued', 'processing', 'failed'].includes(j.status))
                  .slice(0, 8)
                  .map(j => {
                    const labels = { queued: 'En cola', processing: 'Procesando', failed: 'Error' };
                    const dot = j.status === 'failed'
                      ? 'bg-red-500'
                      : j.status === 'processing'
                        ? 'bg-emerald-500'
                        : 'bg-amber-400';
                    return (
                      <div
                        key={j.id}
                        data-testid={`job-row-${j.id}`}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/55 border border-gray-200/50"
                      >
                        <span className={`w-2 h-2 rounded-full shrink-0 ${dot} ${j.status === 'processing' ? 'animate-pulse' : ''}`} />
                        <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 shrink-0">
                          {j.status === 'processing' && <Loader2 className="w-3 h-3 animate-spin" />}
                          {labels[j.status]}
                        </span>
                        <span className="text-[11px] text-gray-400 shrink-0">
                          {j.mode === 'edit' ? 'edit' : 'gen'} · {j.variations}x · {j.aspect_ratio}
                        </span>
                        <span className="text-sm text-gray-700 truncate flex-1" title={j.prompt}>
                          {j.prompt}
                        </span>
                        {j.status === 'failed' && j.error_message && (
                          <span
                            className="text-[11px] text-red-500 max-w-[260px] truncate"
                            title={j.error_message}
                          >
                            {j.error_message}
                          </span>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            {error && <p className="text-xs text-red-500">{error}</p>}

            {/* Results grid */}
            {results.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider">Resultados</h3>
                <div className={`grid gap-3 ${results.length === 1 ? 'grid-cols-1 max-w-md' : results.length <= 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                  {results.map((r, i) => {
                    const aspectClass =
                      r.aspect_ratio === 'vertical' ? 'aspect-[2/3]'
                      : r.aspect_ratio === 'horizontal' ? 'aspect-[3/2]'
                      : 'aspect-square';
                    const src = r.url || (r.base64 ? `data:image/png;base64,${r.base64}` : null);
                    const isSavedToProject = savedIdxs[r.id];
                    const isSentToVideo = savedIdxs[`v-${r.id}`];
                    return (
                      <div
                        key={r.id}
                        data-testid={`gen-result-${i}`}
                        onClick={() => setSelectedIdx(i)}
                        className={`relative rounded-2xl overflow-hidden cursor-pointer border-2 transition-all hover:shadow-lg bg-white/40 ${
                          selectedIdx === i ? 'border-gray-900 shadow-md' : 'border-transparent'
                        }`}
                      >
                        {src && (
                          <img
                            src={src}
                            alt={`Variación ${i + 1}`}
                            className={`w-full ${aspectClass} object-cover`}
                          />
                        )}
                        {selectedIdx === i && (
                          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center">
                            <Check className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}

                        {/* Per-image action bar */}
                        <div className="absolute inset-x-0 bottom-0 px-2.5 py-2 bg-gradient-to-t from-black/55 via-black/20 to-transparent flex items-center justify-between gap-1.5">
                          <span className="text-[10px] text-white font-medium">v{i + 1}</span>
                          <div className="flex items-center gap-1.5">
                            <button
                              data-testid={`gen-download-${i}`}
                              title="Descargar"
                              onClick={(e) => { e.stopPropagation(); handleDownload(r); }}
                              className="p-1.5 rounded-lg bg-white/25 hover:bg-white/40 backdrop-blur-sm transition-colors"
                            >
                              <Download className="w-3.5 h-3.5 text-white" />
                            </button>
                            <button
                              data-testid={`gen-save-${i}`}
                              title={projectId ? 'Guardar al proyecto' : 'Selecciona un proyecto'}
                              disabled={!projectId || savingIdx === i || isSavedToProject}
                              onClick={(e) => { e.stopPropagation(); saveToProject(i); }}
                              className={`p-1.5 rounded-lg backdrop-blur-sm transition-colors ${
                                isSavedToProject
                                  ? 'bg-emerald-500/90'
                                  : 'bg-white/25 hover:bg-white/40 disabled:opacity-40'
                              }`}
                            >
                              {savingIdx === i ? (
                                <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                              ) : isSavedToProject ? (
                                <Check className="w-3.5 h-3.5 text-white" />
                              ) : (
                                <FolderPlus className="w-3.5 h-3.5 text-white" />
                              )}
                            </button>
                            <button
                              data-testid={`gen-tovideo-${i}`}
                              title="Enviar a generación de video"
                              onClick={(e) => { e.stopPropagation(); sendToVideoGen(r); }}
                              className={`p-1.5 rounded-lg backdrop-blur-sm transition-colors ${
                                isSentToVideo
                                  ? 'bg-indigo-500/90'
                                  : 'bg-white/25 hover:bg-white/40'
                              }`}
                            >
                              <Film className="w-3.5 h-3.5 text-white" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* History tab */
          <div className="p-4 md:p-6 max-w-4xl">
            {!historyLoaded ? (
              <div className="text-center py-16 text-gray-400">
                <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin opacity-40" />
                <p className="text-sm">Cargando historial...</p>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Image className="w-10 h-10 mx-auto mb-3 opacity-20" />
                <p className="text-sm">Aún no hay imágenes generadas.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {history.map((asset, i) => (
                  <a
                    key={asset.id || i}
                    href={asset.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group rounded-2xl overflow-hidden border border-gray-200/40 bg-white/30 hover:shadow-md transition-all"
                  >
                    <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200/50 flex items-center justify-center overflow-hidden">
                      {asset.url ? (
                        <img src={asset.url} alt={asset.name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                      ) : (
                        <Image className="w-8 h-8 text-gray-300" />
                      )}
                    </div>
                    <div className="px-3 py-2.5">
                      <span className="text-[11px] text-gray-700 font-medium block truncate">{asset.name}</span>
                      <span className="text-[10px] text-gray-400">
                        {asset.created_at ? new Date(asset.created_at).toLocaleDateString() : ''}
                      </span>
                      {asset.project_id && (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-600 mt-0.5">
                          <Link2 className="w-3 h-3" /> Vinculada
                        </span>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
